import { Button } from '@heroui/react'
import type { ModalComponentProps } from '@qse/constants'
import { Check, Crown, DollarSign, Shield, Star, Zap } from 'lucide-react'

interface BillingProduct {
  id: string
  name: string
  description: string
  active: boolean
  defaultPrice: {
    id: string
    amount: number
    currency: string
    interval: string
    intervalCount: number
  }
  features: string[]
  createdAt: number
  updatedAt: number
  metadata: Record<string, any>
}

interface Plan {
  id: string
  name: string
  description: string
  stripePlanId: string
  storageQuotaGb: number
  serviceUsageQuota: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  billingProduct: BillingProduct
}

const formatPrice = (amount: number, currency: string): string => {
  const price = amount / 100 // Convert from cents

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(price)
}

const getPlanIcon = (planName: string) => {
  const name = planName.toLowerCase()

  if (name.includes('pro') || name.includes('premium')) return Crown
  if (name.includes('extreme') || name.includes('enterprise')) return Star
  if (name.includes('basic') || name.includes('starter')) return Shield

  return Zap
}

const getPlanGradient = (index: number) => {
  const gradients = [
    'from-blue-500 to-purple-600',
    'from-purple-500 to-pink-600',
    'from-pink-500 to-red-600',
    'from-green-500 to-blue-600',
    'from-yellow-500 to-orange-600',
    'from-indigo-500 to-purple-600',
  ]

  return gradients[index % gradients.length]
}

export default function BillingModal(props: ModalComponentProps) {
  const { data } = props
  const plans: Plan[] = data?.plans || []

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
            <DollarSign className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
          Billing Plans
        </h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Choose the perfect plan for your needs. All plans include our core features with varying
          limits and advanced capabilities.
        </p>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {plans.map((plan, index) => {
          const IconComponent = getPlanIcon(plan.billingProduct.name)
          const gradient = getPlanGradient(index)
          const isPopular = plan.billingProduct.name.toLowerCase().includes('pro')

          return (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-6 border-2 transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                isPopular
                  ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              }`}
            >
              {/* Popular Badge */}
              {isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className={`inline-flex p-3 rounded-full bg-gradient-to-r ${gradient} mb-4`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {plan.billingProduct.name}
                </h3>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                  {formatPrice(
                    plan.billingProduct.defaultPrice.amount,
                    plan.billingProduct.defaultPrice.currency,
                  )}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    /{plan.billingProduct.defaultPrice.interval}
                  </span>
                </div>
              </div>

              {/* Plan Description */}
              <div
                dangerouslySetInnerHTML={{ __html: plan.billingProduct.description }}
                className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center"
              />

              {/* Plan Specs */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Storage
                  </span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {plan.storageQuotaGb} GB
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    API Calls
                  </span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {plan.serviceUsageQuota.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Features */}
              {plan.billingProduct.features && plan.billingProduct.features.length > 0 && (
                <div className="space-y-2 mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Features:</h4>
                  {plan.billingProduct.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Status */}
              <div className="flex items-center justify-center mb-4">
                <div
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    plan.billingProduct.active
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-[#DD524C]'
                  }`}
                >
                  {plan.billingProduct.active ? 'Active' : 'Inactive'}
                </div>
              </div>

              {/* Action Button */}
              <Button
                size="lg"
                className={`w-full font-medium ${
                  isPopular
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                    : 'bg-gray-900 dark:bg-gray-700 text-white hover:bg-gray-800 dark:hover:bg-gray-600'
                }`}
              >
                {plan.billingProduct.active ? 'Select Plan' : 'Contact Sales'}
              </Button>
            </div>
          )
        })}
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        <p>All plans include 24/7 support and a 30-day money-back guarantee.</p>
        <p className="mt-1">
          Need a custom plan?{' '}
          <button className="text-blue-500 hover:text-blue-600 font-medium">
            Contact our sales team
          </button>
        </p>
      </div>
    </div>
  )
}
